import React, { useState } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import api from '../services/api';
import { useCart } from '../context/CartContext';
import { useNavigate } from 'react-router-dom';
import { useIsMobile } from '../hooks/useResponsive';
import toast from 'react-hot-toast';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY || 'pk_test_VOTRE_CLE');

const VD    = '#6D28D9';
const V     = '#8B5CF6';
const GOLD  = '#C9A96E';
const OM    = '#FF6900';
const GREEN = '#059669';
const serif = "'Cormorant Garamond', Georgia, serif";
const sans  = "'Montserrat', sans-serif";
const OM_NUMBER = '+221 77 376 96 21';

const METHODS = [
  { val: 'stripe',       label: 'Carte',          icon: '💳', color: V,     sub: 'Visa · Mastercard' },
  { val: 'orange_money', label: 'Orange Money',   icon: '🟠', color: OM,    sub: OM_NUMBER },
  { val: 'cash',         label: 'À la livraison', icon: '🚚', color: GREEN, sub: 'Payez à réception' },
];

function CheckoutForm() {
  const stripe   = useStripe();
  const elements = useElements();
  const { items, total, shippingCost, clearCart } = useCart();
  const navigate  = useNavigate();
  const isMobile  = useIsMobile();
  const [loading, setLoading]  = useState(false);
  const [paymentMethod, setPM] = useState('orange_money');
  const [omPhone, setOmPhone]  = useState('');
  const [address, setAddress]  = useState({ nom: '', rue: '', ville: '', codePostal: '', telephone: '' });

  const grandTotal = total + shippingCost;

  const inp = {
    background: '#F8F7FF', border: '1px solid #E9E4FF', color: '#111',
    padding: '12px 14px', fontSize: 13, width: '100%',
    boxSizing: 'border-box', outline: 'none', borderRadius: 4, fontFamily: sans,
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const orderRes = await api.post('/api/orders', {
        items: items.map(i => ({ productId: i.productId, size: i.size, quantity: i.quantity })),
        shippingAddress: { ...address, pays: 'Sénégal' },
      });
      const orderId = orderRes.data._id;

      if (paymentMethod === 'stripe') {
        if (!stripe || !elements) return;
        const { data: { clientSecret } } = await api.post('/api/payments/create-intent', { amount: grandTotal, orderId });
        const { error } = await stripe.confirmCardPayment(clientSecret, {
          payment_method: { card: elements.getElement(CardElement) },
        });
        if (error) throw new Error(error.message);
        clearCart();
        toast.success('Paiement confirmé !');
        navigate('/mes-commandes');
      } else if (paymentMethod === 'orange_money') {
        await api.post('/api/payments/orange-money', { orderId, amount: grandTotal, phone: omPhone });
        clearCart();
        navigate(`/paiement-orange-money?orderId=${orderId}&amount=${grandTotal}`);
      } else {
        await api.post('/api/payments/cash-delivery', { orderId });
        clearCart();
        toast.success('Commande confirmée ! Paiement à la livraison.');
        navigate('/mes-commandes');
      }
    } catch (err) {
      toast.error(err.response?.data?.message || err.message || 'Erreur');
    } finally {
      setLoading(false);
    }
  };

  const activeColor = METHODS.find(m => m.val === paymentMethod)?.color || V;

  return (
    <div style={{ maxWidth: 600, margin: '0 auto', padding: isMobile ? '1.5rem 1rem 4rem' : '3rem 1.5rem 5rem' }}>

      <div style={{ textAlign: 'center', marginBottom: 32 }}>
        <p style={{ fontSize: 10, letterSpacing: 5, color: GOLD, textTransform: 'uppercase', fontFamily: sans, fontWeight: 600, marginBottom: 8 }}>✦ SS Parfumerie ✦</p>
        <h1 style={{ fontFamily: serif, fontSize: isMobile ? 28 : 40, fontWeight: 400, color: VD }}>Finaliser la commande</h1>
      </div>

      {/* Numéro */}
      <div style={{ background: 'linear-gradient(135deg, #1A0533, #3B0764)', borderRadius: 8, padding: '1rem 1.25rem', marginBottom: 28, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
        <div>
          <p style={{ fontSize: 9, letterSpacing: 3, color: GOLD, textTransform: 'uppercase', fontFamily: sans, fontWeight: 600, marginBottom: 4 }}>Besoin d'aide ?</p>
          <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.7)', fontFamily: sans, fontWeight: 300 }}>Appelez-nous</p>
        </div>
        <a href={`tel:${OM_NUMBER.replace(/\s/g,'')}`} style={{ fontFamily: serif, fontSize: isMobile ? 18 : 22, color: GOLD, textDecoration: 'none' }}>
          {OM_NUMBER}
        </a>
      </div>

      <form onSubmit={handleSubmit}>
        {/* Adresse */}
        <div style={{ marginBottom: 28 }}>
          <p style={{ fontSize: 9, letterSpacing: 4, color: '#AAA', textTransform: 'uppercase', fontFamily: sans, fontWeight: 600, marginBottom: 14 }}>① Adresse de livraison</p>
          <div style={{ display: 'grid', gap: 10 }}>
            {[['nom','Nom complet *'],['rue','Adresse / Quartier *'],['ville','Ville *'],['codePostal','Code postal'],['telephone','Téléphone *']].map(([k, label]) => (
              <input key={k} placeholder={label} value={address[k]}
                onChange={e => setAddress(p => ({ ...p, [k]: e.target.value }))}
                style={inp} required={['nom','rue','ville','telephone'].includes(k)} />
            ))}
          </div>
        </div>

        {/* Paiement */}
        <div style={{ marginBottom: 24 }}>
          <p style={{ fontSize: 9, letterSpacing: 4, color: '#AAA', textTransform: 'uppercase', fontFamily: sans, fontWeight: 600, marginBottom: 14 }}>② Mode de paiement</p>
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)', gap: 10, marginBottom: 16 }}>
            {METHODS.map(({ val, label, icon, color, sub }) => (
              <button key={val} type="button" onClick={() => setPM(val)} style={{
                padding: isMobile ? '12px 16px' : '14px 8px',
                border: `2px solid ${paymentMethod===val ? color : '#EEE'}`,
                background: paymentMethod===val ? `${color}10` : '#fff',
                color: paymentMethod===val ? color : '#888',
                cursor: 'pointer', borderRadius: 8, fontFamily: sans,
                display: 'flex', flexDirection: isMobile ? 'row' : 'column',
                alignItems: 'center', gap: isMobile ? 12 : 6,
                textAlign: isMobile ? 'left' : 'center',
              }}>
                <span style={{ fontSize: isMobile ? 24 : 24 }}>{icon}</span>
                <div>
                  <div style={{ fontSize: 11, fontWeight: 600 }}>{label}</div>
                  <div style={{ fontSize: 10, opacity: 0.7 }}>{sub}</div>
                </div>
              </button>
            ))}
          </div>

          {paymentMethod === 'stripe' && (
            <div style={{ background: '#F8F7FF', border: '1px solid #DDD6FE', borderRadius: 8, padding: '1rem 1.25rem' }}>
              <CardElement options={{ style: { base: { fontSize: '14px', color: '#111', fontFamily: 'Montserrat, sans-serif' } } }} />
            </div>
          )}

          {paymentMethod === 'orange_money' && (
            <div style={{ border: '1px solid rgba(255,105,0,0.3)', borderRadius: 8, overflow: 'hidden' }}>
              <div style={{ background: 'linear-gradient(135deg, #FF6900, #FF8C00)', padding: '1.25rem 1.5rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
                <div>
                  <p style={{ fontSize: 10, letterSpacing: 2, color: 'rgba(255,255,255,0.8)', textTransform: 'uppercase', fontFamily: sans, marginBottom: 6 }}>Numéro marchand</p>
                  <p style={{ fontFamily: serif, fontSize: isMobile ? 22 : 28, color: '#fff', letterSpacing: 2 }}>{OM_NUMBER}</p>
                </div>
                <span style={{ fontSize: 36 }}>🟠</span>
              </div>
              <div style={{ padding: '1rem 1.5rem', background: '#FFF8F3' }}>
                <p style={{ fontSize: 12, color: '#888', marginBottom: 10, fontFamily: sans }}>
                  Envoyez <strong style={{ color: OM }}>{grandTotal?.toLocaleString()} FCFA</strong> puis entrez votre numéro.
                </p>
                <input placeholder="Votre numéro Orange Money" value={omPhone}
                  onChange={e => setOmPhone(e.target.value)} style={{ ...inp, background: '#fff' }} required />
              </div>
            </div>
          )}

          {paymentMethod === 'cash' && (
            <div style={{ background: '#F0FDF4', border: '1px solid #A7F3D0', borderRadius: 8, padding: '1.25rem 1.5rem', display: 'flex', gap: 14, alignItems: 'flex-start' }}>
              <span style={{ fontSize: 28, flexShrink: 0 }}>🚚</span>
              <div>
                <p style={{ fontSize: 14, fontWeight: 600, color: GREEN, fontFamily: sans, marginBottom: 6 }}>Paiement à la livraison</p>
                <p style={{ fontSize: 12, color: '#555', lineHeight: 1.7, fontFamily: sans, fontWeight: 300 }}>
                  Vous payez en espèces au livreur lors de la réception.
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Récapitulatif */}
        <div style={{ background: '#FDFCFF', border: '1px solid #EDE9FE', borderRadius: 8, padding: '1.25rem', marginBottom: 20 }}>
          <p style={{ fontSize: 9, letterSpacing: 4, color: '#AAA', textTransform: 'uppercase', fontFamily: sans, fontWeight: 600, marginBottom: 12 }}>③ Récapitulatif</p>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, color: '#888', marginBottom: 6, fontFamily: sans }}>
            <span>Sous-total</span><span>{total?.toLocaleString()} FCFA</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, color: '#888', marginBottom: 12, fontFamily: sans }}>
            <span>Livraison</span>
            <span style={{ color: shippingCost===0 ? '#059669' : '#111' }}>{shippingCost===0 ? 'Offerte ✓' : `${shippingCost?.toLocaleString()} FCFA`}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderTop: '1px solid #EDE9FE', paddingTop: 12 }}>
            <span style={{ fontFamily: sans, fontSize: 13, fontWeight: 600, color: '#444' }}>Total</span>
            <span style={{ fontFamily: serif, fontSize: 28, color: VD }}>{grandTotal?.toLocaleString()} <span style={{ fontSize: 13, fontFamily: sans, fontWeight: 300, color: '#AAA' }}>FCFA</span></span>
          </div>
        </div>

        <button type="submit" disabled={loading} style={{
          width: '100%', background: loading ? '#AAA' : activeColor,
          color: '#fff', border: 'none', padding: '16px',
          fontSize: 11, letterSpacing: 3, textTransform: 'uppercase',
          cursor: loading ? 'not-allowed' : 'pointer',
          fontFamily: sans, fontWeight: 700, borderRadius: 6,
        }}>
          {loading ? 'Traitement...' : paymentMethod==='cash'
            ? `✓ Confirmer — ${grandTotal?.toLocaleString()} FCFA`
            : paymentMethod==='orange_money'
              ? `Valider par Orange Money`
              : `Payer par carte — ${grandTotal?.toLocaleString()} FCFA`}
        </button>
      </form>
    </div>
  );
}

export default function CheckoutPage() {
  return (
    <div style={{ background: '#fff', minHeight: '100vh', color: '#111' }}>
      <Elements stripe={stripePromise}>
        <CheckoutForm />
      </Elements>
    </div>
  );
}
